import { useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';

interface ConfirmDialogProps {
    open: boolean;
    title: string;
    /** Consequência da ação, em uma ou duas frases. */
    description: string;
    confirmLabel: string;
    cancelLabel: string;
    busy?: boolean;
    danger?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

// Confirmação que diz o que vai acontecer; os botões repetem a ação ("Excluir aula" / "Manter aula").
export function ConfirmDialog({ open, title, description, confirmLabel, cancelLabel, busy = false, danger = true, onConfirm, onCancel }: ConfirmDialogProps) {
    const cancelRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!open) return;
        // Foco no botão seguro: Enter sem querer não apaga nada
        cancelRef.current?.focus();
        const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onCancel(); };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, busy, onCancel]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}>
            <div role="alertdialog" aria-modal="true" aria-labelledby="confirm-title" aria-describedby="confirm-desc" className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md p-6">
                <h2 id="confirm-title" className="text-lg font-bold text-slate-900 dark:text-white">{title}</h2>
                <p id="confirm-desc" className="mt-2 text-sm whitespace-pre-line text-slate-600 dark:text-slate-300">{description}</p>
                <div className="mt-6 flex justify-end gap-3">
                    <button ref={cancelRef} onClick={onCancel} disabled={busy} className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                        {cancelLabel}
                    </button>
                    <button onClick={onConfirm} disabled={busy} className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${danger ? 'bg-red-600 hover:bg-red-700 focus-visible:ring-red-500' : 'bg-blue-600 hover:bg-blue-700 focus-visible:ring-blue-500'}`}>
                        {busy && <Loader2 size={15} className="animate-spin" aria-hidden />}
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}
